import * as React from 'react'
import {
  AUTOFILL_METADATA_SUGGESTION_COMMENT_KEY,
  WorkflowBlock,
  WorkflowRun,
  isDocumentLikeBlock,
} from '@basehub/data/replicache/index'
import * as PropertiesPanel from '../../properties-panel'
import {
  ChevronDownIcon,
  PlayIcon,
  PlusIcon,
  SymbolIcon,
  Cross2Icon,
  ArrowRightIcon,
} from '@radix-ui/react-icons'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { format } from 'date-fns'
import { useGetTabPrefix, useTabsStore } from '../../tabs/store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useRepoStore } from '../../repo-store'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { useRepoMembers } from '~/hooks-data/use-repo-members'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import {
  useBranchOrCommitSegment,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { useUserStore } from '~/app/_store/user-store'
import { toast } from 'sonner'
import { useEditorMode } from '../../editor-mode-provider'
import {
  useIsGuest,
  useIsGuestInOthersBranch,
} from '~/hooks-data/use-user-role'
import { WorkflowRunIcon } from './run-icon'

export const WorkflowBlockPropertiesPanel = ({
  block,
}: {
  block: WorkflowBlock
}) => {
  const replicache = useRepoReplicache()?.data?.replicache
  const [limit, setLimit] = React.useState(8)
  const [newEventsAvailable, setNewEventsAvailable] = React.useState(true)
  const selectedBranchOrCommitQuery = useSelectedBranchOrCommitQuery()
  const editorMode = useEditorMode()
  const isGuest = useIsGuest()
  const replicacheOrUndefined = React.useMemo(() => {
    if (!selectedBranchOrCommitQuery.data) return undefined
    if (isGuest) return replicache
    if (editorMode === 'read' || editorMode === 'preview') return undefined
    return replicache
  }, [editorMode, isGuest, replicache, selectedBranchOrCommitQuery.data])
  const { data } = useCustomSyncerSub(replicacheOrUndefined, {
    syncer: 'workflowRuns',
    params: {
      workflowBlock: block,
      limit,
      branchId: selectedBranchOrCommitQuery.data?.id ?? '',
    },
  })

  React.useEffect(() => {
    if (!data) return
    if (data.length < limit) setNewEventsAvailable(false)
  }, [limit, data])

  const runs = data ?? []

  if ((editorMode === 'read' || editorMode === 'preview') && !isGuest) {
    return null
  }

  return (
    <PropertiesPanel.Group title="Runs">
      {Boolean(runs.length) ? (
        <div>
          <PropertiesPanel.Accordion>
            {runs.map((run) => (
              <WorkflowRunAccordion key={run.id} run={run} />
            ))}
          </PropertiesPanel.Accordion>
          {newEventsAvailable && (
            <Button
              icon={<PlusIcon />}
              iconSide="left"
              focusStyled
              className="text-xs text-dark-faint text-left py-2 flex items-center gap-1 font-medium cursor-default focus-visible:outline-offset-0 w-full hover:text-dark-dim"
              unstyled
              onClick={() => {
                setLimit((prev) => prev + 8)
              }}
            >
              Load more
            </Button>
          )}
        </div>
      ) : (
        <p className="text-xs text-dark-faint text-pretty">
          This workflow wasn't triggered yet.
        </p>
      )}
    </PropertiesPanel.Group>
  )
}

const WorkflowRunAccordion = ({ run }: { run: WorkflowRun }) => {
  const date = new Date(run.updatedAt ?? run.createdAt)
  const timeDiff = useTimeDiff(date)
  const isGuestInOthersBranch = useIsGuestInOthersBranch()
  const metadataCopy = { ...run.metadata }
  const commentId = metadataCopy[AUTOFILL_METADATA_SUGGESTION_COMMENT_KEY]
  if (AUTOFILL_METADATA_SUGGESTION_COMMENT_KEY in metadataCopy) {
    delete metadataCopy[AUTOFILL_METADATA_SUGGESTION_COMMENT_KEY]
  }

  return (
    <PropertiesPanel.AccordionItem
      key={run.id}
      value={run.id}
      className="border-b border-dark-border-solid group"
    >
      <PropertiesPanel.AccordionTrigger className="flex py-2 justify-between items-center w-full text-xs whitespace-nowrap overflow-hidden text-ellipsis gap-x-1 font-medium text-dark-bright">
        <span className="flex items-center flex-1 gap-x-1 justify-between">
          <span className="overflow-hidden text-ellipsis">{timeDiff}</span>
          <SimpleTooltip content={run.status} contentClassName="capitalize">
            <span>
              <WorkflowRunIcon withBg run={run} />
            </span>
          </SimpleTooltip>
        </span>
        <ChevronDownIcon className="ml-auto radix-state-closed:rotate-180 shrink-0 size-[15px] group-radix-state-closed:-rotate-90 transition-transform" />
      </PropertiesPanel.AccordionTrigger>
      <PropertiesPanel.AccordionContent className="space-y-1.5">
        {/* date */}
        <div className="text-[12px]">
          <p className="font-medium text-dark-faint">Date</p>
          <p className="text-dark-dim">
            {format(date, 'MMM d, yyyy. h:mm:ss a')}
          </p>
        </div>

        {/* trigger */}
        <div className="text-[12px]">
          <p className="font-medium text-dark-faint">Trigger</p>
          <WorkflowRunTriggerLink run={run} />
        </div>

        {/* action */}
        <div className="text-[12px]">
          <p className="font-medium text-dark-faint">Action</p>
          <WorkflowRunActionLink run={run} />
        </div>

        {/* metadata */}
        <div className="text-[12px]">
          <p className="font-medium text-dark-faint mb-1">Metadata</p>
          <ScrollArea asChild variant="subtle" orientation="horizontal">
            <pre
              tabIndex={-1}
              className="text-dark-dim rounded-md bg-dark-shade py-1.5 px-2"
            >
              <code>{JSON.stringify(metadataCopy, null, 2)}</code>
            </pre>
          </ScrollArea>
        </div>

        {commentId && (
          <div className="text-[12px]">
            <p className="font-medium text-dark-faint">Content suggestion</p>
            <WorkflowRunActionLink run={run} asSuggestion />
          </div>
        )}

        {/* controls */}
        {!isGuestInOthersBranch && <RunControls run={run} />}

        <br />
      </PropertiesPanel.AccordionContent>
    </PropertiesPanel.AccordionItem>
  )
}

const RunControls = ({ run }: { run: WorkflowRun }) => {
  const replicache = useRepoReplicache()?.data?.replicache
  const rootBlockId = useWorkingTreeStore((s) => s.tree?.root.value.id)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const optimisticUser = useUserStore((s) => s.optimisticUser)

  if (run.status === 'success' || run.status === 'stopped') {
    return null
  }

  if (run.status === 'error') {
    return (
      <RunControl
        onClick={async () => {
          if (!replicache || !rootBlockId) return
          const newTrigger = run.trigger
          switch (newTrigger.type) {
            case 'layout-block.updated': {
              if (!getNode) return
              const updatedBlockId =
                newTrigger.data.updatedBlockId ||
                (newTrigger.data.updatedBlock?.id ?? '')
              const updatedBlock = getNode(updatedBlockId)?.value

              if (!updatedBlock) {
                let message =
                  'The block was deleted, generation cannot be retried'

                if (run.action.type === 'ai-autofill-variant') {
                  message =
                    'The variant was deleted, generation cannot be retried'
                }
                toast.error(message)
                return
              }

              break
            }
            default:
              break
          }

          await replicache.mutate.runWorkflows({
            ids: [run.id],
            branchId: run.branchId,
            isoNow: new Date().toISOString(),
            metadata: {},
            rootBlockId,
          })
        }}
        icon={<SymbolIcon />}
      >
        Retry
      </RunControl>
    )
  }

  return (
    <div className="flex items-center">
      <RunControl
        onClick={async () => {
          if (!replicache || !rootBlockId) return
          await replicache.mutate.updateWorkflowRun({
            id: run.id,
            data: {
              status: 'stopped',
              metadata: {
                info: `${optimisticUser?.username} stopped this workflow run`,
                stoppedAt: format(new Date(), 'MMM d, yyyy. h:mm:ss a'),
              },
            },
            rootBlockId,
            isoNow: new Date().toISOString(),
          })
        }}
        icon={<Cross2Icon />}
      >
        Stop
      </RunControl>
      {run.status === 'pending' && (
        <RunControl
          onClick={async () => {
            if (!replicache || !rootBlockId) return
            await replicache.mutate.runWorkflows({
              ids: [run.id],
              branchId: run.branchId,
              isoNow: new Date().toISOString(),
              metadata: {},
              rootBlockId,
            })
          }}
          icon={<PlayIcon />}
        >
          Run now
        </RunControl>
      )}
    </div>
  )
}

const RunControl = (
  props: Pick<
    React.ComponentProps<typeof Button>,
    'icon' | 'onClick' | 'children'
  >
) => {
  return (
    <Button
      {...props}
      className="cursor-default text-xs flex items-center px-1.5 py-0.5 gap-x-1 hover:text-dark-dim font-medium text-dark-faint rounded"
      focusStyled
      iconSide="right"
      unstyled
    />
  )
}

const WorkflowRunTriggerLink = ({ run }: { run: WorkflowRun }) => {
  const addTab = useTabsStore((s) => s.addTab)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const setFocus = useRepoStore((s) => s.setFocus)

  let children = ''
  let onClick = () => {}
  switch (run.trigger.type) {
    case 'commit.created': {
      const commitId = run.trigger.data.id
      children = 'A commit happened'
      onClick = () => {
        addTab({ key: `commit:${commitId}`, partiallyOpen: true })
      }

      break
    }
    case 'list-block.created':
    case 'list-block.deleted':
    case 'list-block.updated': {
      if (!getNode) break
      const sourceBlockId = idToSourceBlockId(run.trigger.data.listBlockId, {
        getNode,
      })

      children = 'A row was created'
      onClick = () => addTab({ key: sourceBlockId, partiallyOpen: true })
      break
    }
    case 'block.updated':
    case 'layout-block.updated': {
      if (!getNode) break
      const id =
        'updatedBlockId' in run.trigger.data
          ? run.trigger.data.updatedBlockId ||
            (run.trigger.data.updatedBlock?.id ?? '')
          : run.trigger.data.blockId
      const node = getNode(id)
      if (!node) {
        children = 'A block was updated'
        onClick = () => {}
        break
      }

      const updatedBlock = node.value
      const sourceBlockId = idToSourceBlockId(updatedBlock)

      children = isDocumentLikeBlock(updatedBlock)
        ? 'A block title was updated'
        : 'A block was updated'
      const hasUpdatedTitle = isDocumentLikeBlock(updatedBlock)
      onClick = () => {
        if (!sourceBlockId) return
        const focusType = hasUpdatedTitle
          ? 'block:document-title'
          : node.parentKey
            ? 'block:wrapper'
            : 'block:document-title'
        setFocus({
          type: focusType,
          blockId: sourceBlockId,
          preventScroll: focusType === 'block:document-title',
        })
        addTab({
          key: hasUpdatedTitle
            ? sourceBlockId
            : (node.parentKey ?? sourceBlockId),
          partiallyOpen: true,
        })
      }
      break
    }
    case 'event-block.created': {
      if (!getNode) break
      const sourceBlockId = idToSourceBlockId(run.trigger.data.eventBlockId, {
        getNode,
      })

      children = 'An event was received'
      onClick = () => addTab({ key: sourceBlockId, partiallyOpen: true })
      break
    }
    default:
      return <p className="text-dark-dim">Action not specified</p>
  }

  return (
    <Button
      unstyled
      className="text-dark-dim hover:underline"
      focusStyled
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

const WorkflowRunActionLink = ({
  run,
  asSuggestion = false,
}: {
  run: WorkflowRun
  asSuggestion?: boolean
}) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const { data: repoMembers } = useRepoMembers()
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const addTab = useTabsStore((s) => s.addTab)
  const tabPrefix = useGetTabPrefix()
  const setFocus = useRepoStore((s) => s.setFocus)
  const treeManager = useWorkingTreeStore((s) => s.tree)

  switch (run.action.type) {
    case 'ai-autofill':
    case 'ai-autofill-variant': {
      let targetedBlockId = run.action.blockId ?? ''
      if (
        run.action.type === 'ai-autofill-variant' &&
        run.trigger.type === 'layout-block.updated'
      ) {
        targetedBlockId = run.trigger.data.layoutBlockId
      }
      const targetedNode = getNode?.(targetedBlockId)
      const targetedBlock = targetedNode?.value

      let action = 'is being generated'
      if (run.status === 'success') {
        action = ` was generated`
      } else if (run.status === 'error') action = 'generation failed'
      else if (run.status === 'stopped') action = 'generation was stopped'
      else if (run.status === 'pending') action = 'generation is scheduled'

      const blockOrVariant =
        run.action.type === 'ai-autofill-variant' ? 'variant' : 'block'

      const suggestionCommentId =
        AUTOFILL_METADATA_SUGGESTION_COMMENT_KEY in run.metadata
          ? run.metadata[AUTOFILL_METADATA_SUGGESTION_COMMENT_KEY]
          : undefined

      if (
        (asSuggestion && !suggestionCommentId) ||
        !targetedBlock ||
        !targetedNode ||
        !targetedNode.parentKey ||
        !treeManager
      ) {
        if (asSuggestion) return null

        const tooltip = `This action targeted a ${blockOrVariant} that was deleted.`

        return (
          <SimpleTooltip content={tooltip} contentClassName="text-pretty">
            <p className="max-w-max text-dark-dim">
              A {blockOrVariant} {action}
            </p>
          </SimpleTooltip>
        )
      }

      const targetedSourceBlockId = idToSourceBlockId(targetedBlock)
      const parentSourceBlockId = idToSourceBlockId(
        targetedNode.parentKey,
        treeManager
      )

      const href =
        run.action.type === 'ai-autofill-variant'
          ? `/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${targetedSourceBlockId}${suggestionCommentId ? `?commentId=${suggestionCommentId}` : ''}`
          : `/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${parentSourceBlockId}${suggestionCommentId ? `?commentId=${suggestionCommentId}` : ''}#${targetedSourceBlockId}`

      if (asSuggestion) {
        return (
          <ButtonLink
            unstyled
            focusStyled
            className="flex items-center justify-between bg-dark-shade hover:bg-dark-shade-hover text-dark-accent font-medium text-xs px-2 py-1 cursor-default"
            href={href}
            iconSide="right"
            onClick={() => {
              setFocus({
                type: 'block:wrapper',
                blockId: targetedSourceBlockId,
              })
            }}
          >
            Review suggestion
            <ArrowRightIcon />
          </ButtonLink>
        )
      }

      return (
        <ButtonLink
          unstyled
          focusStyled
          className="hover:underline max-w-max text-dark-dim block"
          href={href}
          onClick={() => {
            setFocus({
              type: 'block:wrapper',
              blockId: targetedSourceBlockId,
            })
            addTab({
              key: `${tabPrefix}${run.action.type === 'ai-autofill-variant' ? targetedSourceBlockId : parentSourceBlockId}`,
              partiallyOpen: true,
            })
          }}
        >
          "{targetedBlock.title || 'Untitled'}" {action}
        </ButtonLink>
      )
    }

    case 'webhook': {
      const tooltip = `This action targeted ${run.action.url || 'no URL'}`

      let action = 'was just called'
      if (run.status === 'success') action = 'was called'
      else if (run.status === 'error') action = 'call failed'
      else if (run.status === 'stopped') action = 'call was stopped'
      else if (run.status === 'pending') action = 'call was scheduled'

      return (
        <SimpleTooltip content={tooltip} contentClassName="text-pretty">
          <p className="max-w-max text-dark-dim">A webhook {action}</p>
        </SimpleTooltip>
      )
    }

    case 'notification': {
      let action = 'is being'
      if (run.status === 'pending') {
        if (run.action.userIds.length > 1) {
          action = 'are being'
        } else {
          action = 'is being'
        }
      } else {
        if (run.action.userIds.length > 1) {
          action = 'were'
        } else {
          action = 'was'
        }
      }

      const notifiedMembers = run.action.userIds.map((userId) => {
        const member = repoMembers?.find((member) => member.id === userId)
        if (!member) return null
        return member.username
      })

      return (
        <p className="max-w-max text-dark-dim">
          {run.action.userIds.length > 1
            ? `${run.action.userIds.length} users ${action}`
            : `${notifiedMembers} ${action}`}{' '}
          notified
        </p>
      )
    }
    default:
      return <p className="text-dark-dim">Action not specified</p>
  }
}
